export const TIMEOUT_STRING = "timeout";
export const AMAZON_SUFFIX = "@amazon.com";

// API Gateway constants
export const API_GATEWAY_PROCESS_NAME = "rnrRCCService";
export const DEFAULT_PLAN_VERSION = "STATIC_V1";

// ListYardTrailers endpoint constants
export const LIST_YARD_TRAILERS_VISIT_REASON = "INBOUND";
export const LIST_YARD_TRAILERS_LOCALE = "en-US";

// Plan Status constants
export enum PlanStatus {
    PENDING_CREATION = "PENDING_CREATION",
    PENDING_APPROVAL = "PENDING_APPROVAL",
    PENDING_USER_INPUT = "PENDING_USER_INPUT",
    RECEIVED_USER_INPUT = "RECEIVED_USER_INPUT",
    APPROVED = "APPROVED",
    FAILED = "FAILED",
    REJECTED = "REJECTED",
    CANCELLED = "CANCELLED",
    CLOSED = "CLOSED"
}

// Origin of a request. Sent by the read-only published-plan page so the backend
// relaxes the closed-plan validation and the frontend renders CLOSED plans.
export const REQUEST_SOURCE_PUBLISHED_PLAN = "PUBLISHED_PLAN";

// Dark Mode constants
export enum DARK_MODE {
    LIGHT = "LIGHT",
    DARK = "DARK"
}

// Dark Mode value type (used by ThemeProvider, themeSlice, bosonClient, store types)
export type DarkModeValue = 'LIGHT' | 'DARK';

// Theme Mode constants
export type ThemeMode = "light" | "dark" | "auto";

export enum ErrorCode {
    PLAN_OWNED_BY_ANOTHER_USER = "PLAN_OWNED_BY_ANOTHER_USER"
}

export const UNABLE_TO_CREATE_PLAN = "Unable to create plan";

export const COULD_NOT_PROCEEED_WITH_PLAN_CREATION = "Could not proceed with plan creation";

// Work Items Dashboard constants
export const WORK_ITEMS_PAGE_SIZE = 10;
// Lookback window for the work items dashboard, in days.
// Capped at 14 to stay strictly under MASS's 15-day DDB anomaly-entity TTL.
// MASS's GetAnomaliesByIndexActivityHandler short-circuits to an empty list
// when startTime is older than `now - 15 days`, so a value >= 15 produces
// silent empty responses for ACTIVE queries even though RESOLVED/EXPIRED
// queries (which use a shorter window) still succeed.
export const WORK_ITEMS_START_DATE_DAYS = 14;
export const WORK_ITEMS_ENDPOINT = "/getWorkItems";
export const WORK_ITEM_COUNTS_ENDPOINT = "/getWorkItemCounts";
export const LOAD_WORK_ITEM_ENDPOINT = "/loadWorkItem";
export const UPDATE_WORK_ITEM_STATE_ENDPOINT = "/updateWorkItemState";

export enum WorkItemStatus {
    ACTIVE = "ACTIVE",
    RESOLVED = "RESOLVED",
    EXPIRED = "EXPIRED"
}

// Reducer lifecycle status for the Work Items Dashboard page. Distinct from
// `WorkItemStatus` above (which is the API enum on a single work item).
export enum WorkItemsStatus {
    IDLE = "idle",
    LOADING_COUNTS = "loading_counts",
    LOADING_ITEMS = "loading_items",
    READY = "ready",
    ERROR = "error"
}

// Reducer lifecycle status for the Work Item Detail Page. Tracks both the
// LoadWorkItem and UpdateWorkItemState (Resolve) thunks. Mirrors the
// `WorkItemsStatus` pattern above so call sites reference enum members
// rather than ad-hoc string literals.
export enum WorkItemDetailStatus {
    IDLE = "idle",
    LOADING = "loading",
    FULFILLED = "fulfilled",
    REJECTED = "rejected"
}

// Agent Chat modes
export enum AgentMode {
  SHIFT_TRACKING = 'shiftTracking',
  SHIFT_PLAN = 'shiftPlan',
}

// Agent Chat — max question length (characters)
export const MAX_QUESTION_LENGTH = 2000;

// Shift Plan Agent — plan selection phases
export enum ShiftPlanAgentPhase {
  CONFIRMING = 'confirming',
  AWAITING_INPUT = 'awaiting_input',
  FETCHING_PLANS = 'fetching_plans',
  SELECTING_PLAN = 'selecting_plan',
  ACTIVE = 'active',
}

// ── Route path constants ──────────────────────────────────────────────────────
export const ROUTES = {
  HOME: "/",
  SHIFT_PLAN_ROSTER: "/shift-plan/roster",
  SHIFT_PLAN_INBOUND_TRAILERS: "/shift-plan/inbound-trailers",
  SHIFT_PLAN_VOLUMES: "/shift-plan/volumes",
  SHIFT_PLAN_CHECKOUT: "/shift-plan/checkout",
  SHIFT_PLAN_REVIEW: "/shift-plan/review",
  INBOUND: "/inbound",
  SHIFT_ROTATION: "/shift-rotation",
  SHIFT_ROTATION_SHIFT_ROSTER: "/shift-rotation/shift-roster",
  SHIFT_ROTATION_PLANNED_HC: "/shift-rotation/planned-headcount",
  SHIFT_ROTATION_ASSIGNMENTS: "/shift-rotation/assignments",
  SHIFT_ROTATION_REVIEW: "/shift-rotation/review-and-publish",
  SHIFT_ROTATION_ASSOCIATE_VIEW: "/shift-rotation/associate-view",
  SHIFT_ROTATION_HISTORY: "/shift-rotation/history",
  SHIFT_ROTATION_PUBLISHED: "/shift-rotation/published/:planId",
  WORK_ITEMS: "/work-items",
  WORK_ITEM_DETAIL: "/work-items/detail",
  LEADERBOARD: "/leaderboard",
  IMAGE_QUALITY: "/image-quality",
  ASSOCIATE_SCORECARDS: "/associate-scorecards",
  REPORTING: "/reporting",
  FLOW_MAP: "/flow-map",
  BUFFERS: "/buffers",
} as const;

// Leaderboard badge IDs
export const BADGE_IDS = {
  TOP_SITES: 'topSites',
  FIRST_TIME_ADOPTER: 'firstTimeAdopter',
  CONSISTENT_SITES: 'consistentSites',
} as const;

export interface ShiftOption {
  value: string;
  label: string;
}

export const DEFAULT_SHIFT_OPTIONS: ShiftOption[] = [
  { value: 'day', label: 'Day' },
  { value: 'night', label: 'Night' },
];

export const DEFAULT_SHIFT_HALF_OPTIONS: ShiftOption[] = [
  { value: 'firstHalfStationAssignment', label: 'First half' },
  { value: 'secondHalfStationAssignment', label: 'Second half' },
];
